import { FormValueScope } from '@epages/react-components'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import { convertSizingFromPxToPercent } from './HeaderSettings'
import { useTranslate, withComponents } from '../../utils/translate'
import FormValue from '../formComponents/FormValue'
import themeSettingsFields from './themeSettingsFields'
import useHelpCenterLink from '../../utils/hooks/useHelpCenterLink'

type Props = {
  disabled?: boolean
  linkDisabled?: boolean
}

export default function LogoAdjustmentThemeSettings({ disabled, linkDisabled }: Readonly<Props>) {
  const themeSettingsShopLogoSchema = useSelector<State, ImmutableMap>((state) =>
    state.get('themeSettingsSchema').find((s) => s.get('name') === 'logoAdjustments'),
  )
  const defaultThemeSettings = useSelector<State, ImmutableMap>((state) => state.get('defaultThemeSettings'))

  const helpCenterUrl = useHelpCenterLink('SHOP_LOGO')

  const t = useTranslate('interface', 'components.bottomBar.headerSettings.shopLogo.shopLogoAdjustments')

  const scopedName = 'themeSettings'
  const logoAdjustmentDefaultValue = defaultThemeSettings.get('logoAdjustments')

  const setting = themeSettingsShopLogoSchema
  const logoAdjustmentFields = useMemo(() => {
    const fields = themeSettingsFields(t, defaultThemeSettings)
    return fields[setting.get('type')]({
      setting,
      isTopLevel: true,
      disabled,
    }) as ReactNode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultThemeSettings, setting, disabled])

  return (
    <>
      <FormValueScope name={scopedName}>
        <fieldset>
          <legend>{t('title')}</legend>
          <div className="dali-settingslayer-element">
            {withComponents(t)('infoText', {
              link: (
                <a
                  className={cc(['ep-form-row-text-external-link', { disabled: linkDisabled }])}
                  href={helpCenterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('linkText')}
                </a>
              ),
            })}
          </div>
          {logoAdjustmentFields}
        </fieldset>
      </FormValueScope>
      <FormValue name={`${scopedName}.logoAdjustments`}>
        {(_value: any, setValue: (n: any) => void) => (
          <button
            type="button"
            disabled={disabled}
            className="dali-settingslayer-logo-adjustments-reset-button"
            onClick={() =>
              setValue(logoAdjustmentDefaultValue.updateIn(['sizing'], (v) => convertSizingFromPxToPercent(v)))
            }
          >
            {t('resetButton.label')}
          </button>
        )}
      </FormValue>
    </>
  )
}
